import type { UsageSheetLinkField } from './types/forge-usage-sheet.ts';

/**
 * S1 — a tool proposed on each object of an app's list (« Ensuite »), as read
 * by the object's drawer. Declared once in the Forge from either side; the
 * button opens the tool's form prefilled from the object, nothing runs alone.
 */
export interface AppToolLinkInput {
  key: string;
  label: string;
  required: boolean;
  defaultValue?: string;
}

export interface AppToolLink {
  id: string;
  sourceAppId: string;
  target: { flowId: string; title: string; appKey: string; inputs: AppToolLinkInput[] };
  fields: UsageSheetLinkField[];
  /** Status keys at which the object is ready; empty = every status. */
  statuses: string[];
}

interface CellLike {
  key: string;
  value: string | number | boolean | null;
}

interface ColumnLike {
  key: string;
  label: string;
}

const same = (left: string, right: string): boolean =>
  left.trim().toLocaleLowerCase('fr') === right.trim().toLocaleLowerCase('fr');

/** The tool is offered on objects at one of its statuses (all when none is set). */
export const isLinkOffered = (link: Pick<AppToolLink, 'statuses'>, status: string): boolean =>
  link.statuses.length === 0 || link.statuses.includes(status);

/** A field of the tool, found by its key, then by its exact label. */
const findInput = (inputs: AppToolLinkInput[], field: UsageSheetLinkField): AppToolLinkInput | undefined =>
  inputs.find((input) => input.key === field.input)
  ?? inputs.find((input) => same(input.label, field.input) || same(input.label, field.inputLabel));

/** A cell of the object, found by its column key, then by the column's exact label. */
const findCell = (cells: CellLike[], columns: ColumnLike[], field: UsageSheetLinkField): CellLike | undefined => {
  const direct = cells.find((cell) => cell.key === field.column);
  if (direct) return direct;
  const column = columns.find((item) => same(item.label, field.column) || same(item.label, field.columnLabel));
  return column ? cells.find((cell) => cell.key === column.key) : undefined;
};

/**
 * The tool's form values taken from the object. A field whose input or column
 * is not found stays empty: the person fills it in the form.
 */
export const buildLinkPrefill = (
  link: Pick<AppToolLink, 'fields' | 'target'>,
  cells: CellLike[],
  columns: ColumnLike[],
): Record<string, string> => {
  const prefill: Record<string, string> = {};
  for (const field of link.fields) {
    const input = findInput(link.target.inputs, field);
    const cell = findCell(cells, columns, field);
    if (!input || !cell || cell.value === null) continue;
    const value = String(cell.value).trim();
    if (value) prefill[input.key] = value;
  }
  return prefill;
};

/** What a launch sends: the tool's defaults, then the object's values. */
export const linkLaunchValues = (link: Pick<AppToolLink, 'target'>, prefill: Record<string, string>): Record<string, string> => ({
  ...Object.fromEntries(link.target.inputs.flatMap((input) => (input.defaultValue ? [[input.key, input.defaultValue]] : []))),
  ...prefill,
});

/** Required fields left empty by the object and the tool's defaults (the form asks for them). */
export const missingRequiredInputs = (link: Pick<AppToolLink, 'target'>, prefill: Record<string, string>): AppToolLinkInput[] => {
  const values = linkLaunchValues(link, prefill);
  return link.target.inputs.filter((input) => input.required && !values[input.key]?.trim());
};
